import React, { useCallback, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ApiService } from "../../../../api";
import CommitmentDetails from "../commitment-details/CommitmentDetails";
import CommitmentComments from "../commitment-comments/CommitmentComments";
import CommitmentSidePanel from "../commitment-sidepanel/CommitmentSidePanel";
import CommitmentHistory from "../commitment-history/CommitmentHistory";
import { fetchCommitments, updateThemeList, setRightPanelView, setPage } from "../../slice/commitmentTrackingSlice";
import { buildFilterPayload } from "../../utils";

const CommitmentRightPanel = () => {
	const dispatch = useDispatch();
	const projectId = useSelector(({ context }) => context?.project?.id);
	const cancelTokenSourceRef = useRef(null);
	const selectedCommitment = useSelector(({ commitmentTracking }) => commitmentTracking.selectedCommitment);
	const selectedTheme = useSelector(({ commitmentTracking }) => commitmentTracking.selectedTheme);
	const filterDefinitions = useSelector(({ commitmentTracking }) => commitmentTracking.filterDefinitions);
	const appliedFilters = useSelector(({ commitmentTracking }) => commitmentTracking.appliedFilters);
	const themeFieldUuid = useSelector(({ commitmentTracking }) => commitmentTracking.themeFieldUuid);
	const themes = useMemo(
		() =>
			filterDefinitions.find((filterDefinition) => filterDefinition.fieldName === "theme")?.possibleValues || [],
		[filterDefinitions]
	);
	const rightPanelView = useSelector(({ commitmentTracking }) => commitmentTracking.rightPanelView);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	const handleClose = useCallback(() => {
		dispatch(setRightPanelView("details"));
	}, [dispatch]);
	const handleEditSubmit = useCallback(async () => {
		try {
			const cancelToken = cancelTokenSourceRef.current?.token;
			const filtersForThemeList = { ...appliedFilters };
			if (themeFieldUuid && filtersForThemeList[themeFieldUuid]) {
				delete filtersForThemeList[themeFieldUuid];
			}
			await dispatch(
				updateThemeList({
					projectId,
					filters: filtersForThemeList,
					themeFieldUuid,
					cancelToken,
				})
			).unwrap();
			if (selectedTheme && themeFieldUuid) {
				const searchPayload = buildFilterPayload(filterDefinitions, appliedFilters);
				const selectedThemeData = themes?.find((t) => t.value === selectedTheme);
				const resolvedThemeUuid = selectedThemeData?.valueUuid ? [selectedThemeData?.valueUuid] : [];
				const themeField = filterDefinitions.find((field) => field.fieldUuid === themeFieldUuid);
				const themeFieldName = themeField ? themeField.fieldName.toLowerCase().replace(/ /g, "_") : "theme";
				searchPayload[themeFieldName] = {
					is: true,
					matchAll: true,
					elements: resolvedThemeUuid,
				};
				dispatch(setPage(0));
				await dispatch(
					fetchCommitments({
						projectId,
						payload: searchPayload,
						page: 0,
						limit: 25,
						cancelToken,
					})
				).unwrap();
			}
			handleClose();
		} catch (error) {
			console.error("Error refreshing after edit:", error);
		}
	}, [dispatch, projectId, themeFieldUuid, filterDefinitions, appliedFilters, selectedTheme, themes, handleClose]);

	return (
		<>
			<CommitmentDetails />
			<CommitmentComments
				commitmentId={selectedCommitment?.commitmentId}
				open={rightPanelView === "comments"}
				onClose={handleClose}
			/>
			<CommitmentSidePanel
				commitment={selectedCommitment}
				filterDefinitions={filterDefinitions}
				mode="edit"
				open={rightPanelView === "edit"}
				projectId={projectId}
				onClose={handleClose}
				onSubmit={handleEditSubmit}
			/>
			<CommitmentHistory
				commitmentId={selectedCommitment?.commitmentId}
				open={rightPanelView === "history"}
				onClose={handleClose}
			/>
		</>
	);
};

export default CommitmentRightPanel;
